import React, {useEffect} from "react";
import {useSelector} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css} from "@emotion/react";
import lscache from "lscache";

import {Container} from "@pg-design/grid-module";
import {mb, onDesktop, pt} from "@pg-design/helpers-css";
import {ARTICLE_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {parseSearch} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";

import {Breadcrumbs} from "../../app/components/breadcrumbs/Breadcrumbs";
import {BreadcrumbsItem} from "../../app/components/breadcrumbs/BreadcrumbsItem";
import {IRPStore} from "../../app/rp_reducer";
import {SeoMainHeadline} from "../../atoms/SeoMainHeadline";
import {ArticleCategory, getArticleCategoryBySlug, getNameByArticleCategory} from "../actions/ArticleCategory";
import {IArticleListArticle} from "../actions/fetch_article_list";
import {ArticleListCategoryDescription} from "../components/article_list/ArticleListCategoryDescription";
import {ArticleListFeaturedArticles} from "../components/article_list/ArticleListFeaturedList";
import {ArticleListLatestList} from "../components/article_list/ArticleListLatestList";
import {ArticleCategoriesList} from "../components/ArticleCategoriesList";
import {ArticleContainer} from "../components/ArticleContainer";
import {ArticleEbookSection} from "../components/ArticleEbookSection";
import {ArticleSearchBar} from "../components/ArticleSearchBar";
import {SeeInvestmentRanking} from "../components/SeeInvestmentRanking";
import {SeeMoreCategories} from "../components/SeeMoreCategories/SeeMoreCategories";
import {IArticleListStore} from "../reducers/article_list_reducer";

export interface IArticleCategoryParams {
    categorySlug: string;
}

interface IStateProps {
    list: IArticleListStore;
}

interface IActionsProps {}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const RECOMMENDED_ARTICLES_LIMIT = 2;

export const ArticleCategoryListView: React.FC<IProps> = (props) => {
    const {categorySlug} = props.match.params as IArticleCategoryParams;
    const categoryNumber = getArticleCategoryBySlug(categorySlug);
    const page = parseSearch(props.location.search)?.page;
    const phrase = parseSearch(props.location.search)?.phrase;
    const isFirstPage = page === undefined;

    const articlesList = useSelector((state: IRPStore) => state.article.list);

    const isDuringSearchCategory = ArticleCategory.DuringSearch === categoryNumber;
    const isAfterPurchaseCategory = ArticleCategory.AfterPurchase === categoryNumber;

    const {articles, recommendedArticles} = splitArticlesAndRecommendedForCategory(articlesList, categoryNumber);

    useEffect(() => {
        lscache.flushExpired();
    }, []);

    return (
        <article>
            <ArticleSearchBar />
            <Container>
                <Breadcrumbs css={mb(3)} homePageUrl={rpAppLink.base()}>
                    <BreadcrumbsItem isLinkingToCurrentLocation={!categoryNumber} name="Wiadomości" position="2" url={rpAppLink.article.list()} />
                    {!!categoryNumber && (
                        <BreadcrumbsItem
                            isLinkingToCurrentLocation
                            name={getNameByArticleCategory(categoryNumber)}
                            position="3"
                            url={rpAppLink.article.category({categorySlug: categorySlug})}
                        />
                    )}
                </Breadcrumbs>
            </Container>
            {!phrase && (
                <>
                    <ArticleContainer>
                        {!!categoryNumber && (
                            <SeoMainHeadline pageString={page && ` - strona ${page}`} dataTestId={ARTICLE_TEST_IDS.HEADLINES.CATEGORY_NAME} css={mb(3)}>
                                {`${getNameByArticleCategory(categoryNumber)}${page ? ` - strona ${page}` : ""}`}
                            </SeoMainHeadline>
                        )}
                        <ArticleListCategoryDescription
                            css={[mb(5), onDesktop(mb(8))]}
                            description={articlesList.category?.seo_description}
                            phrases={articlesList.category?.phrases ?? []}
                        />
                    </ArticleContainer>

                    {isDuringSearchCategory && <ArticleEbookSection />}

                    {isFirstPage ? (
                        <ArticleContainer>
                            <ArticleListFeaturedArticles
                                css={[mb(5), pt(4), onDesktop(mb(6))]}
                                articles={[...articlesList.articlesFeatured, ...recommendedArticles]}
                            />
                        </ArticleContainer>
                    ) : null}

                    {isDuringSearchCategory && (
                        <SeeMoreCategories
                            css={[mb(4), onDesktop(mb(8))]}
                            categories={[ArticleCategory.Taxes, ArticleCategory.Reports, ArticleCategory.Finance]}
                        />
                    )}
                    {isAfterPurchaseCategory && (
                        <SeeMoreCategories css={[mb(4), onDesktop(mb(8))]} categories={[ArticleCategory.Finance, ArticleCategory.Taxes]} />
                    )}
                </>
            )}
            <ArticleContainer>
                <ArticleListLatestList
                    phrase={phrase}
                    showHeader={isFirstPage}
                    articles={phrase ? articlesList.articles : articles}
                    count={articlesList.count - recommendedArticles.length}
                    location={props.location}
                />

                <SeeInvestmentRanking />
            </ArticleContainer>

            <ArticleContainer css={categoriesListStyles}>
                <ArticleCategoriesList />
            </ArticleContainer>
        </article>
    );
};

const categoriesListStyles = css`
    background: #fff;
    ${onDesktop(css`
        background: transparent;
    `)}
`;

const splitArticlesAndRecommendedForCategory = (articlesListStore: IArticleListStore, currentCategory: ArticleCategory | null) =>
    articlesListStore.articles.reduce<{articles: IArticleListArticle[]; recommendedArticles: IArticleListArticle[]}>(
        (accumulator, article) => {
            const isRecommendedBelowLimit = accumulator.recommendedArticles.length + articlesListStore.articlesFeatured.length < RECOMMENDED_ARTICLES_LIMIT;

            return currentCategory && article.featured_categories.includes(currentCategory) && isRecommendedBelowLimit
                ? {
                      ...accumulator,
                      recommendedArticles: [...accumulator.recommendedArticles, article]
                  }
                : {...accumulator, articles: [...accumulator.articles, article]};
        },
        {
            articles: [],
            recommendedArticles: []
        }
    );
import React, {useEffect} from "react";
import {useSelector} from "react-redux";
import {RouteComponentProps} from "react-router";
import {css} from "@emotion/react";
import lscache from "lscache";

import {Container} from "@pg-design/grid-module";
import {mb, onDesktop, pt} from "@pg-design/helpers-css";
import {ARTICLE_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {parseSearch} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";

import {Breadcrumbs} from "../../app/components/breadcrumbs/Breadcrumbs";
import {BreadcrumbsItem} from "../../app/components/breadcrumbs/BreadcrumbsItem";
import {IRPStore} from "../../app/rp_reducer";
import {SeoMainHeadline} from "../../atoms/SeoMainHeadline";
import {ArticleCategory, getArticleCategoryBySlug, getNameByArticleCategory} from "../actions/ArticleCategory";
import {IArticleListArticle} from "../actions/fetch_article_list";
import {ArticleListCategoryDescription} from "../components/article_list/ArticleListCategoryDescription";
import {ArticleListFeaturedArticles} from "../components/article_list/ArticleListFeaturedList";
import {ArticleListLatestList} from "../components/article_list/ArticleListLatestList";
import {ArticleCategoriesList} from "../components/ArticleCategoriesList";
import {ArticleContainer} from "../components/ArticleContainer";
import {ArticleEbookSection} from "../components/ArticleEbookSection";
import {ArticleSearchBar} from "../components/ArticleSearchBar";
import {SeeInvestmentRanking} from "../components/SeeInvestmentRanking";
import {SeeMoreCategories} from "../components/SeeMoreCategories/SeeMoreCategories";
import {IArticleListStore} from "../reducers/article_list_reducer";

export interface IArticleCategoryParams {
    categorySlug: string;
}

interface IStateProps {
    list: IArticleListStore;
}

interface IActionsProps {}

interface IOwnProps extends RouteComponentProps<{}> {}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const RECOMMENDED_ARTICLES_LIMIT = 2;

export const ArticleCategoryListView: React.FC<IProps> = (props) => {
    const {categorySlug} = props.match.params as IArticleCategoryParams;
    const categoryNumber = getArticleCategoryBySlug(categorySlug);
    const page = parseSearch(props.location.search)?.page;
    const phrase = parseSearch(props.location.search)?.phrase;
    const isFirstPage = page === undefined;

    const articlesList = useSelector((state: IRPStore) => state.article.list);

    const isDuringSearchCategory = ArticleCategory.DuringSearch === categoryNumber;
    const isAfterPurchaseCategory = ArticleCategory.AfterPurchase === categoryNumber;

    const {articles, recommendedArticles} = splitArticlesAndRecommendedForCategory(articlesList, categoryNumber);

    useEffect(() => {
        lscache.flushExpired();
    }, []);

    return (
        <article>
            <ArticleSearchBar />
            <Container>
                <Breadcrumbs css={mb(3)} homePageUrl={rpAppLink.base()}>
                    <BreadcrumbsItem isLinkingToCurrentLocation={!categoryNumber} name="Wiadomości" position="2" url={rpAppLink.article.list()} />
                    {!!categoryNumber && (
                        <BreadcrumbsItem
                            isLinkingToCurrentLocation
                            name={getNameByArticleCategory(categoryNumber)}
                            position="3"
                            url={rpAppLink.article.category({categorySlug: categorySlug})}
                        />
                    )}
                </Breadcrumbs>
            </Container>
            {!phrase && (
                <>
                    <ArticleContainer>
                        {!!categoryNumber && (
                            <SeoMainHeadline pageString={page && ` - strona ${page}`} dataTestId={ARTICLE_TEST_IDS.HEADLINES.CATEGORY_NAME} css={mb(3)}>
                                {`${getNameByArticleCategory(categoryNumber)}${page ? ` - strona ${page}` : ""}`}
                            </SeoMainHeadline>
                        )}
                        <ArticleListCategoryDescription
                            css={[mb(5), onDesktop(mb(8))]}
                            description={articlesList.category?.seo_description}
                            phrases={articlesList.category?.phrases ?? []}
                        />
                    </ArticleContainer>

                    {isDuringSearchCategory && <ArticleEbookSection />}

                    {isFirstPage ? (
                        <ArticleContainer>
                            <ArticleListFeaturedArticles
                                css={[mb(5), pt(4), onDesktop(mb(6))]}
                                articles={[...articlesList.articlesFeatured, ...recommendedArticles]}
                            />
                        </ArticleContainer>
                    ) : null}

                    {isDuringSearchCategory && (
                        <SeeMoreCategories
                            css={[mb(4), onDesktop(mb(8))]}
                            categories={[ArticleCategory.Taxes, ArticleCategory.Reports, ArticleCategory.Finance]}
                        />
                    )}
                    {isAfterPurchaseCategory && (
                        <SeeMoreCategories css={[mb(4), onDesktop(mb(8))]} categories={[ArticleCategory.Finance, ArticleCategory.Taxes]} />
                    )}
                </>
            )}
            <ArticleContainer>
                <ArticleListLatestList
                    phrase={phrase}
                    showHeader={isFirstPage}
                    articles={phrase ? articlesList.articles : articles}
                    count={articlesList.count - recommendedArticles.length}
                    location={props.location}
                />

                <SeeInvestmentRanking />
            </ArticleContainer>

            <ArticleContainer css={categoriesListStyles}>
                <ArticleCategoriesList />
            </ArticleContainer>
        </article>
    );
};

const categoriesListStyles = css`
    background: #fff;
    ${onDesktop(css`
        background: transparent;
    `)}
`;

const splitArticlesAndRecommendedForCategory = (articlesListStore: IArticleListStore, currentCategory: ArticleCategory | null) =>
    articlesListStore.articles.reduce<{articles: IArticleListArticle[]; recommendedArticles: IArticleListArticle[]}>(
        (accumulator, article) => {
            const isRecommendedBelowLimit = accumulator.recommendedArticles.length + articlesListStore.articlesFeatured.length < RECOMMENDED_ARTICLES_LIMIT;

            return currentCategory && article.featured_categories.includes(currentCategory) && isRecommendedBelowLimit
                ? {
                      ...accumulator,
                      recommendedArticles: [...accumulator.recommendedArticles, article]
                  }
                : {...accumulator, articles: [...accumulator.articles, article]};
        },
        {
            articles: [],
            recommendedArticles: []
        }
    );
